import Routing from "routing";

import type { MessageDescriptor } from "@lingui/core";
import { defineMessage as dm } from "@lingui/core/macro";

import type { ResultTypeDefinition, SearchHit } from "./types";

const asString = (value: unknown): string | undefined =>
  "string" === typeof value && value.length > 0 ? value : undefined;

const resultTypeRegistry: Partial<Record<string, ResultTypeDefinition>> = {
  contacts: {
    icon: "User",
    label: dm`Contacts`,
    getSubtitle: (hit) => asString(hit.organizationName),
  },
  clients: {
    icon: "Building2",
    label: dm`Clients`,
    getHref: (hit) => Routing.generate("azuris_client_index", { path: hit.id }),
  },
  employees: {
    icon: "IdCard",
    label: dm`Employees`,
    getSubtitle: (hit) => asString(hit.department),
    getHref: (hit) => Routing.generate("azuris_employee_edit", { id: hit.id }),
  },
  projects: {
    icon: "Briefcase",
    label: dm`Projects`,
    getSubtitle: (hit) => asString(hit.clientName),
  },
  locations: {
    icon: "MapPin",
    label: dm`Locations`,
    getSubtitle: (hit) =>
      [asString(hit.street), asString(hit.city)].filter(Boolean).join(", ") ||
      undefined,
  },
  suppliers: {
    icon: "Truck",
    label: dm`Suppliers`,
  },
  subcontractors: {
    icon: "HardHat",
    label: dm`Subcontractors`,
  },
  addresses: {
    icon: "MapPinned",
    label: dm`Addresses`,
    getSubtitle: (hit) =>
      [asString(hit.postalCode), asString(hit.city)]
        .filter(Boolean)
        .join(", ") || undefined,
  },
};

export function getResultTypeDefinition(
  indexUid: string,
): ResultTypeDefinition | undefined {
  return resultTypeRegistry[indexUid];
}

/**
 * The full list of filterable index names + labels, in the order they
 * should be offered in the type filter — matches
 * `GlobalSearchController::INDEX_WEIGHTS`.
 */
export function getResultTypeOptions(): {
  value: string;
  label: MessageDescriptor;
}[] {
  return (
    Object.entries(resultTypeRegistry) as [string, ResultTypeDefinition][]
  ).map(([value, definition]) => ({ value, label: definition.label }));
}

export function getResultTitle(hit: SearchHit): string {
  return asString(hit.displayName) ?? asString(hit.name) ?? `#${hit.id}`;
}

export function getResultHref(hit: SearchHit): string | undefined {
  return resultTypeRegistry[hit._federation.indexUid]?.getHref?.(hit);
}
